import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";
import { SitemapVersionLevel } from "@iventis/domain-model/model/sitemapVersionLevel";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { polygon } from "@turf/helpers";
import intersect from "@turf/intersect";

// Returns a list of sitemaps which are currently in viewport
export const getSitemapLevelsWithinBoundsAndLevelIndex = (bounds: number[][][], sitemapVersions: SitemapVersion[], levelIndex: number): SitemapVersionLevel[] => {
    const levelsWithinBoundsAndLevelIndex = [];
    const viewPolygon = polygon(bounds);
    sitemapVersions.forEach((version) => {
        const validLevels = version.sitemapVersionLevels.filter((level) => {
            if (level.levelIndex !== levelIndex) {
                return false;
            }
            const levelPolygon = polygon(level.bounding.coordinates);
            const intersection = intersect(viewPolygon, levelPolygon);
            return intersection;
        });
        validLevels.forEach((level) => levelsWithinBoundsAndLevelIndex.push(level));
    });
    return levelsWithinBoundsAndLevelIndex;
};

// Returns a list of selected sitemap versions based on the map sitemap configs. If there is no record of a sitemap in the map sitemap config, the latest version of the sitemap is assumed to be selected by default.
export const getSelectedSitemapVersions = (sitemaps: Sitemap[], mapSitemapConfigs: MapSitemapConfig[]) => {
    const selectedSitemapVersions: SitemapVersion[] = [];
    sitemaps.forEach((sitemap) => {
        const sitemapConfig = mapSitemapConfigs.find((config) => config.sitemapId === sitemap.id);
        if (sitemapConfig != null) {
            if (sitemapConfig.version2dVisible) {
                const version2d = sitemap.versions.find((v) => v.id === sitemapConfig.version2dId);
                if (version2d != null) {
                    selectedSitemapVersions.push(version2d);
                }
            }
            if (sitemapConfig.version3dVisible) {
                const version3d = sitemap.versions.find((v) => v.id === sitemapConfig.version3dId);
                if (version3d != null) {
                    selectedSitemapVersions.push(version3d);
                }
            }
        } else {
            const latestVersions = getLatestSitemapVersions(sitemap);
            if (latestVersions != null) {
                selectedSitemapVersions.push(...latestVersions);
            }
        }
    });
    return selectedSitemapVersions;
};

// Returns the latest 2D and 3D sitemap versions which have levels and a date defined
const getLatestSitemapVersions = (sitemap: Sitemap): SitemapVersion[] => {
    const versions = [];
    if (!sitemap.versions || sitemap.versions.length === 0) {
        return versions;
    }
    const latest2dVersion = sitemap.versions
        .filter((v) => v.sitemapVersionLevels.length > 0 && v.date && v.type === SitemapVersionType.TwoDimensional)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
    if (latest2dVersion) {
        versions.push(latest2dVersion);
    }
    const latest3dVersion = sitemap.versions
        .filter((v) => v.sitemapVersionLevels.length > 0 && v.date && v.type === SitemapVersionType.ThreeDimensional)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
    if (latest3dVersion) {
        versions.push(latest3dVersion);
    }
    return versions;
};
